import React, { useState, useEffect } from "react";

import ZonesImage from "../../assets/images/zones.jpg";

const TicketCard = ({ name, price, salePrice, description, isLast }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSalePrice = salePrice && salePrice !== price;

  return (
    <div className={`${!isLast ? 'border-b border-surface-100' : ''}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 px-1 text-left group"
      >
        <div className="flex items-center gap-3">
          {/* Ticket icon */}
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500/10 to-pink-500/10 border border-accent-500/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-accent-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
            </svg>
          </div>
          <span className="font-semibold text-surface-900">{name}</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Price */}
          <div className="flex items-center gap-2">
            {hasSalePrice && (
              <span className="text-sm text-red-400 line-through">{salePrice}&#8380;</span>
            )}
            <span className={`text-sm font-bold px-3 py-1 rounded-full ${
              hasSalePrice
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-accent-50 text-accent-700'
            }`}>
              {price}&#8380;
            </span>
          </div>
          {/* Chevron */}
          <svg
            className={`w-4 h-4 text-surface-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </button>
      {/* Expandable content */}
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
        <div className="pl-[52px] text-sm text-surface-500 leading-relaxed" dangerouslySetInnerHTML={{ __html: description }} />
      </div>
    </div>
  );
};

const InfoModal = ({ isOpen, onClose, eventData }) => {
  const [priceMapping, setPriceMapping] = useState({});

  useEffect(() => {
    if (eventData?.data?.ticketTypes) {
      const prices = eventData.data.ticketTypes.reduce((acc, item) => {
        acc[item._id] = item;
        return acc;
      }, {});
      setPriceMapping(prices);
    }
  }, [eventData]);

  if (!isOpen) return null;

  const ticketTypes = eventData?.data?.ticketTypes || [];

  return (
    <div className="relative z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-surface-950/70 backdrop-blur-md" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-surface-100 overflow-hidden">
            {/* Header */}
            <div className="relative px-8 py-6 bg-gradient-to-br from-surface-950 to-surface-800 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-mesh opacity-20" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-accent-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Biletlər haqqında</h3>
                    <p className="text-sm text-white/50">{ticketTypes.length} bilet növü</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-all"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-8 py-2 max-h-[400px] overflow-y-auto">
              {ticketTypes.map(({ _id, name, description }, i) => {
                const ticketData = priceMapping[_id];
                return (
                  <TicketCard
                    key={_id}
                    name={name?.az}
                    price={ticketData?.price || 0}
                    salePrice={ticketData?.salePrice}
                    description={description?.az}
                    isLast={i === ticketTypes.length - 1 && !eventData?.data?.hasZoneInfo}
                  />
                );
              })}

              {/* Zones */}
              {eventData?.data?.hasZoneInfo && (
                <div className="border-t border-surface-100 py-5 px-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-pink-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-surface-900">Zonalar</span>
                  </div>
                  <img
                    src={ZonesImage}
                    alt="Zonalar"
                    className="rounded-2xl w-full"
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-surface-100 bg-surface-50">
              <button
                onClick={onClose}
                className="w-full bg-surface-900 text-white font-semibold py-3 rounded-2xl hover:bg-surface-800 transition-colors text-sm"
              >
                Bağla
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;
